/**
 * Cmembericon Component
 * Displays team member avatar with custom color or icon/text content
 * Supports role-based icons (chair, watchdog, user) or operative model icons
 */
'use client';
import type { CSSProperties, ReactNode } from 'react';
import { useMemo } from 'react';
import { IconCrown, IconShield, IconUser } from '@tabler/icons-react';
import { ColorType, getColorValue } from '@/theme/colors';
import { Ctext } from '../Ctext';
import { Cicon } from '../Cicon';
import { FontWeight } from '@/constants/typography.enum';
import { TextPreset } from '@/constants/componentPresets';
import { TeamRole } from '@cabal/shared/domain/session/team/enums.js';
const USER_ROLE = 'user';
export interface CmembericonProps {
    content?: string | ReactNode;
    role?: TeamRole | typeof USER_ROLE | string; // user is frontend-only for human users
    colorHex?: string;
    size?: number;
    radius?: number;
    className?: string;
    style?: CSSProperties;
}
export function Cmembericon({ content, role, colorHex, size = 44, radius = 18, className, style, }: CmembericonProps) {
    const isTextContent = typeof content === 'string';
    // Map roles to icons
    const getRoleIcon = () => {
        if (!role || role === TeamRole.Operative)
            return null;
        switch (role) {
            case TeamRole.Chair:
                return IconCrown;
            case TeamRole.Watchdog:
                return IconShield;
            case USER_ROLE:
                return IconUser;
            default:
                return null;
        }
    };
    const roleIcon = getRoleIcon();
    const shouldUseRoleIcon = roleIcon !== null;
    const containerStyle: CSSProperties = useMemo(() => {
        let bgColor: string;
        let borderColor: string;
        if (isTextContent) {
            // Text mode: specific rgba values
            bgColor = 'rgba(255, 255, 255, 0.29)';
            borderColor = 'rgba(255, 255, 255, 0.25)';
        }
        else if (colorHex) {
            // Icon mode with custom color
            bgColor = `${colorHex}A6`; // 0.65 opacity in hex
            borderColor = `${colorHex}33`; // 0.2 opacity in hex
        }
        else {
            // Default: no icon, white with 0.4 opacity background
            bgColor = getColorValue(ColorType.White, 0.4);
            borderColor = getColorValue(ColorType.White, 0.2);
        }
        return {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: `${radius}px`,
            backgroundColor: bgColor,
            boxShadow: `rgb(255 255 255 / 17%) 1px 1px 0px 2px inset,rgb(255 255 255 / 13%) -1px -1px 0px 0px inset, rgba(0, 0, 0, 0.3) 0px 2px 5px 0px`,
            overflow: 'hidden',
            flexShrink: 0,
            margin: '1px',
            ...style,
        };
    }, [colorHex, size, radius, style, isTextContent]);
    const renderContent = () => {
        // If role-based icon, render it
        if (shouldUseRoleIcon && roleIcon) {
            return (<Cicon icon={roleIcon} size={size * 0.65} color={ColorType.White} opacity={0.9}/>);
        }
        if (isTextContent) {
            return (<Ctext preset={TextPreset.Regular} size={size * 0.45} weight={FontWeight.SemiBold}>
          {content}
        </Ctext>);
        }
        // Icon/image content - wrap in div with opacity
        return (<div style={{
                opacity: 0.9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
            }}>
        {content}
      </div>);
    };
    return (
      <div className={className} style={containerStyle}>
      {renderContent()}
    </div>
    );
}
